import { Popper } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { Modifier, ModifierArguments } from '@popperjs/core';
import React from 'react';

import { runWhenReady } from '@/utility/runWhenReady';

interface Props {
  readonly value?: string;
  readonly defaultValue?: string;
  readonly label?: string;
  readonly items: readonly string[];
  readonly name?: string;
  readonly error?: boolean;
  readonly helperText?: string;

  readonly fullWidth?: boolean;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
  onSelect?(value: string): void;
}

export const Autocomplete: React.FC<Props> = React.forwardRef<HTMLInputElement, Props>(
  function Autocomplete(props: Props, _ref: React.Ref<HTMLInputElement>): React.ReactElement {
    const { value, items, label, name, fullWidth, error, helperText, onChange, onBlur, onSelect } =
      props;

    const [anchor, setAnchor] = React.useState<HTMLDivElement | null>(null);
    const [expanded, setExpanded] = React.useState<boolean>(false);

    const boxWidth = React.useMemo(
      (): '100%' | undefined => (fullWidth ? '100%' : undefined),
      [fullWidth],
    );

    const clickHandler = React.useCallback(
      (selectedValue: string): VoidFunction => {
        return (): void => {
          const input = anchor?.querySelector<HTMLInputElement>('input');

          if (input) {
            emitInputEvent(input, selectedValue);
          } else {
            const syntheticEvent = {
              target: { value: selectedValue },
              currentTarget: { value: selectedValue },
            } as React.ChangeEvent<HTMLInputElement>;
            // Pretend that it changed
            onChange?.(syntheticEvent);
            // Also call `onSelect` if present
            onSelect?.(selectedValue);
          }
          // Close the menu
          setExpanded(false);
        };
      },
      [anchor, onChange, onSelect],
    );

    const onBlurWrapper = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>): void => {
        setExpanded(false);
        onBlur?.(event);
      },
      [onBlur],
    );

    const onFocus = React.useCallback((): void => {
      if (value && items.includes(value)) {
        return;
      }

      setExpanded(items.length > 0);
    }, [items, value]);

    return (
      <Box sx={styles.container} width={boxWidth} ref={setAnchor}>
        <TextField
          name={name}
          value={value}
          label={label}
          variant="outlined"
          autoComplete="new-password"
          fullWidth={true}
          error={error}
          helperText={helperText}
          onFocus={onFocus}
          onChange={onChange}
          onBlur={onBlurWrapper}
        />

        <Popper
          open={expanded}
          anchorEl={anchor}
          placement="bottom-start"
          modifiers={[customModifier]}>
          <Paper sx={styles.menuPaper} elevation={4}>
            {items.map((option: string): React.ReactNode => {
              return (
                <MenuItem key={option} value={option} onMouseDown={clickHandler(option)}>
                  <Typography variant="body2" color="text.primary" sx={styles.item}>
                    {option}
                  </Typography>
                </MenuItem>
              );
            })}
          </Paper>
        </Popper>
      </Box>
    );
  },
);

const styles: Record<string, SystemStyleObject> = {
  container: {},
  menuPaper: {
    maxHeight: '100%',
    overflowY: 'auto',
  },
  backdrop: {
    position: 'fixed',
    inset: 8,
    zIndex: 1,
  },
  item: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  paper: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  paperContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 4,
  },
};

const customModifier: Modifier<'custom', any> = {
  name: 'custom',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn: (modifierArguments: ModifierArguments<any>): void => {
    const {
      elements: {
        reference,
        popper: { style },
      },
      placement,
    } = modifierArguments.state;
    const { body } = document;

    const anchorRectangle = reference.getBoundingClientRect();
    const maxHeight = body.clientHeight - anchorRectangle.bottom;

    style.width = `${anchorRectangle.width}px`;

    if (maxHeight < 200) {
      const topHeight = anchorRectangle.top - 24;
      style.height = `${topHeight}px`;
      style.display = 'flex';
      style.flexDirection = 'column';
      style.alignItems = 'stretch';
      style.justifyContent = 'flex-end';
      modifierArguments.state.placement = placement.includes('top') ? placement : 'top-start';
    } else {
      style.height = `${maxHeight - 24}px`;
    }
  },
};

const emitInputEvent = (input: HTMLInputElement, value: string): void => {
  /* WARNING - WARNING - WARNING - WARNING
   *
   * Crazy black magic required for react to allow us
   * to induce the triggering of a change event.
   */
  const valueDescriptor = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  );
  const setter = valueDescriptor?.set;
  setter?.call(input, value);

  const event = new Event('input', { bubbles: true });
  input.dispatchEvent(event);

  // Restore focus
  runWhenReady((): void => {
    input.setSelectionRange(value.length, value.length);
    input.focus();
  });
};
